import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { DataTable } from "@/components/DataTable";
import { getItemKey } from "@/stores/DataTableStore";
import { ClearOutlined, DownOutlined, LoadingOutlined, } from "@ant-design/icons";
import { Button, Drawer, Flex, Select } from "antd";
import { observer } from "mobx-react-lite";
import { useCallback, useEffect, useMemo } from "react";
import "./TableSelect.less";
import { Filters } from "../filters";
/**
 * Дефолтный рендерер для Select компонента (обратная совместимость)
 */
const DefaultSelectRenderer = ({ disabled, loading, style, className, isMultiple, hasSelection, selectedItems, placeholder, onClick, onClear, onDeselect, }) => {
    const selectValue = isMultiple
        ? selectedItems.map((item) => item.key)
        : selectedItems[0]?.key;
    const selectOptions = selectedItems.map((item) => (_jsx(Select.Option, { value: item.key, label: item.label, children: item.label }, item.key)));
    return (_jsx(Select, { value: selectValue, placeholder: loading ? "Загрузка..." : placeholder, disabled: disabled || loading, loading: loading, mode: isMultiple ? "multiple" : undefined, allowClear: hasSelection, showSearch: false, open: false, style: style, className: className, suffixIcon: loading ? _jsx(LoadingOutlined, {}) : _jsx(DownOutlined, {}), onClick: onClick, onClear: onClear, optionLabelProp: "label", onDeselect: onDeselect, children: selectOptions }));
};
/**
 * TableSelect - компонент для выбора элементов из таблицы
 * Поддерживает одиночный и множественный выбор, поиск, фильтрацию, сортировку
 * Всегда возвращает string | string[] (ID выбранных элементов)
 */
export const TableSelect = observer(({ value, placeholder = "Выберите элемент", disabled = false, tableSelectStore, dataTableConfig, width = "100%", className, style, allowClear = true, drawerTitle = "Выберите элемент", drawerWidth = "50vw", drawerPlacement = "right", footer, hideSelection, onChange, drawLabel, renderComponent: CustomRenderer = DefaultSelectRenderer, }) => {
    const { rowKey } = dataTableConfig;
    const { dataTableStore } = tableSelectStore;
    // Мемоизированные нормализованные значения
    const safeValue = useMemo(() => {
        const normalizedValue = Array.isArray(value) ? value : [value];
        return normalizedValue.filter(Boolean);
    }, [value]);
    const currentLoading = useMemo(() => dataTableStore.loading || tableSelectStore.loading, [dataTableStore.loading, tableSelectStore.loading]);
    const hasSelection = useMemo(() => dataTableStore.selectedRowKeys.length > 0, [dataTableStore.selectedRowKeys.length]);
    // Инициализация и cleanup
    useEffect(() => {
        tableSelectStore.init();
        return () => tableSelectStore.reset();
    }, []);
    useEffect(() => {
        const selectedKeys = dataTableStore.selectedRowKeys.map(String);
        const needUpdate = JSON.stringify(safeValue) !== JSON.stringify(selectedKeys);
        const needSync = selectedKeys.length === 0 && needUpdate && !tableSelectStore.inited;
        // Синхронизация внешнего value с состоянием store
        if (needSync) {
            dataTableStore.setSelectedRowKeys(safeValue);
            tableSelectStore.setInited(true);
            return;
        }
        if (needUpdate && !needSync) {
            const isEmpty = selectedKeys.length === 0;
            const newValue = tableSelectStore.multiple
                ? selectedKeys
                : selectedKeys[0];
            onChange?.(isEmpty ? undefined : newValue);
        }
    }, [safeValue, dataTableStore.selectedRowKeys]);
    useEffect(() => {
        tableSelectStore.updatePagedData(rowKey);
    }, [rowKey, dataTableStore.data]);
    // обработчики событий
    const handleClear = () => {
        dataTableStore.clearSelection();
    };
    const handleDeselect = (key) => {
        dataTableStore.deselect(key);
    };
    // Подготовка данных для рендерера
    const selectedItemsData = useMemo(() => {
        const optionsMap = new Map(tableSelectStore.pagedData.map((item) => [
            getItemKey(item, rowKey),
            item,
        ]));
        return safeValue
            .map((v) => optionsMap.get(v || ""))
            .filter((item) => Boolean(item))
            .map((item) => {
            const key = getItemKey(item, rowKey);
            const label = drawLabel?.(item) || key;
            return {
                key,
                data: item,
                label,
            };
        });
    }, [rowKey, drawLabel, safeValue, tableSelectStore.pagedData]);
    const { columns, ...otherConfig } = dataTableConfig;
    const visibleFilters = tableSelectStore.filterStore?.getVisibleItems().length || 0;
    const selectionType = hideSelection
        ? undefined
        : tableSelectStore.multiple
            ? "checkbox"
            : "radio";
    const dataTableConfigWithSelection = useMemo(() => ({
        ...otherConfig,
        header: {
            ...otherConfig.header,
            extraContent: otherConfig.header?.extraContent || visibleFilters > 0 ? (_jsxs(Flex, { vertical: true, gap: 16, style: { width: "100%" }, children: [otherConfig.header?.extraContent, visibleFilters > 0 && tableSelectStore.filterStore && (_jsx(Filters, { filtersStore: tableSelectStore.filterStore, title: "\u0424\u0438\u043B\u044C\u0442\u0440\u044B \u0438 \u043F\u043E\u0438\u0441\u043A", showClearAll: true, showApplyButton: false }))] })) : null,
        },
        columns,
        rowKey,
        size: "middle",
        scroll: { y: "100%", x: "100%" },
        tableClassName: "table-select-table",
        selectionType: selectionType,
    }), [otherConfig, columns, rowKey, selectionType, visibleFilters]);
    // Мемоизированные обработчики drawer
    const handleDrawerOpen = useCallback(() => {
        // Выключенный пикер не открывает таблицу: rc-select вешает onClick на контейнер и при disabled
        // (FR-007, UC-104 — клиент корп-заказа в правке заблокирован)
        if (disabled)
            return;
        tableSelectStore.setVisible(true);
    }, [tableSelectStore, disabled]);
    const handleDrawerClose = useCallback(() => {
        tableSelectStore.setVisible(false);
    }, [tableSelectStore]);
    const drawerFooter = useMemo(() => (_jsxs("div", { className: "table-select-footer", style: {
            marginTop: 16,
            textAlign: "center",
            display: "flex",
            gap: "8px",
            justifyContent: "center",
        }, children: [_jsx(Button, { type: "primary", onClick: handleDrawerClose, children: "\u0412\u044B\u0431\u0440\u0430\u0442\u044C" }), allowClear && hasSelection && (_jsx(Button, { type: "link", icon: _jsx(ClearOutlined, {}), onClick: handleClear, size: "small", children: "\u041E\u0447\u0438\u0441\u0442\u0438\u0442\u044C \u0432\u044B\u0431\u043E\u0440" }))] })), [allowClear, hasSelection, handleClear, handleDrawerClose]);
    const getFooter = useMemo(() => {
        if (footer === null) {
            return null;
        }
        if (footer) {
            return footer;
        }
        return drawerFooter;
    }, [footer, drawerFooter]);
    const wrapperClassName = `table-select ${className || ""}`;
    const wrapperStyle = { width, ...style };
    return (_jsxs(_Fragment, { children: [_jsx(CustomRenderer, { disabled: disabled, loading: currentLoading, isMultiple: tableSelectStore.multiple, hasSelection: hasSelection, selectedItems: selectedItemsData, placeholder: placeholder, style: wrapperStyle, className: wrapperClassName, onClick: handleDrawerOpen, onClear: allowClear ? handleClear : undefined, onDeselect: handleDeselect }), _jsx(Drawer, { title: drawerTitle, placement: drawerPlacement, width: drawerWidth, open: tableSelectStore.visible, onClose: handleDrawerClose, destroyOnHidden: true, maskClosable: false, keyboard: true, children: _jsx(DrawerContent, { tableSelectStore: tableSelectStore, dataTableConfig: dataTableConfigWithSelection, footer: getFooter }) })] }));
});
const DrawerContent = observer(({ tableSelectStore, dataTableConfig, footer, }) => {
    useEffect(() => {
        if (tableSelectStore.filterStore) {
            tableSelectStore.dataTableStore.setFilters(tableSelectStore.filterStore.values);
            tableSelectStore.dataTableStore.loadData();
        }
    }, [tableSelectStore.filterStore?.values]);
    return (_jsxs("div", { className: "table-select-drawer", children: [_jsx("div", { className: "table-select-content", children: _jsx(DataTable, { store: tableSelectStore.dataTableStore, config: dataTableConfig }) }), footer] }));
});
